import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { GlobalService } from 'libs/global/global.service';
import { User } from 'libs/user/user.model';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Observable, combineLatest, filter, map, of, switchMap, tap } from 'rxjs';
import { Platform } from '@ionic/angular';
import { SplashScreen } from '@capacitor/splash-screen';
import { environment } from '../environments/environment';
import { UserService } from 'libs/user/user.service';
import { UnsubscriberService } from 'libs/unsubcriber/unsubscriber.service';
import { NavigationEnd, Router } from '@angular/router';
import { AuthorizationService } from 'libs/authorization/authorization.service';
import { NotificationService } from 'libs/notification/notification.service';
import { CONTEXT_LABELS, Context, ContextService, CrossContextIndicator } from 'libs/context/context.service';
import { TrainingService } from 'libs/training/training.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  currentUser$: Observable<User | null> = this.userService.currentUser$.pipe(
    tap((user) => {
      if (user) {
        this.userService.initFcm(user);
        if (this.currentUserRole && this.currentUserRole !== user.role) {
          console.log('CURRENT USER ROLE HAS CHANGED!', user.role);
          // this.unsubscriberService.unsubscribeAll([
          //   'UserService:listenToCurrentUser',
          //   'UserService:fcmToken',
          //   'NotificationService:listenToUserNotificationChanges',
          // ]);
          // Sélecteur de contexte (Sprint 3) : repasse par resolveEntryRoute
          // plutôt que `${user.role}/home` direct, pour respecter un choix de
          // contexte déjà posé cette session et ne pas écraser le rôle
          // sélectionné par une personne multi-rôle.
          this.authorizationService.resolveEntryRoute(user.id, user.role).subscribe((route) => {
            if (route) {
              this.router.navigateByUrl(route, { replaceUrl: true });
            }
          });
        }
        this.currentUserRole = user.role;
      }
    }),
  );
  currentUserRole = '';

  /**
   * Rôle effectif pour l'affichage du menu : le contexte explicitement
   * choisi cette session s'il existe, sinon le rôle légataire — comportement
   * identique à aujourd'hui pour la quasi-totalité des comptes (mono-rôle,
   * jamais de contexte choisi).
   */
  activeContext$: Observable<Context | null> = combineLatest([
    this.userService.currentUser$,
    this.contextService.selectedContext$,
  ]).pipe(map(([user, selected]) => (selected as Context) ?? ((user?.role as Context) || null)));

  /** Indicateur croisé : les AUTRES contextes disponibles, avec leur nombre de notifications en attente. */
  crossContextIndicators$: Observable<CrossContextIndicator[]> = combineLatest([
    this.userService.currentUser$,
    this.activeContext$,
  ]).pipe(
    switchMap(([user, activeContext]) => {
      if (!user) {
        return of([] as CrossContextIndicator[]);
      }
      return combineLatest([
        this.authorizationService.getAvailableContexts(user.id),
        this.authorizationService
          .getActiveTrainingRoleMap(user.id)
          .pipe(switchMap((trainingRoleMap) => this.notificationService.getNotificationCountsByRole(user.id, trainingRoleMap))),
      ]).pipe(
        map(([contexts, counts]) =>
          contexts
            .filter((context) => context !== activeContext)
            .map((context) => ({
              context,
              label: CONTEXT_LABELS[context],
              notificationsCount: counts[context] ?? 0,
            })),
        ),
      );
    }),
  );

  constructor(
    private zone: NgZone,
    private platform: Platform,
    private router: Router,
    private userService: UserService,
    private globalService: GlobalService,
    private unsubscriberService: UnsubscriberService,
    private authorizationService: AuthorizationService,
    private notificationService: NotificationService,
    private contextService: ContextService,
    private trainingService: TrainingService,
  ) {
    this.platform.ready().then(() => {
      this.globalService.customStatusbar();
      SplashScreen.hide();
    });
    this.listenToAppUrlOpen();
    this.trackLoginRoute();
  }

  /** Bascule vers un autre contexte en cours de session (indicateur croisé). */
  switchContext(context: Context) {
    this.contextService.setContext(context);
    this.trainingService.setCurrentTrainingId(null);
    this.router.navigateByUrl(`${context}/home`, { replaceUrl: true });
  }

  /**
   * Pose une classe sur <body> selon qu'on est ou non sur l'ecran de connexion.
   * Remplace le selecteur `ion-app:not(:has(app-login))` de global.scss, que les
   * WebViews Android anterieures a la version 105 ignorent entierement — l'app
   * s'y etirait alors sur toute la largeur d'une tablette.
   * Aucune definition de route n'est modifiee : on ne fait qu'ecouter.
   */
  private trackLoginRoute() {
    const applique = (url: string) => {
      document.body.classList.toggle('is-login-route', url.startsWith('/login'));
    };
    applique(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => applique(e.urlAfterRedirects));
  }

  // FIRED WHEN CLICKING ON MAILTO OR TEL LINKS !!
  // @HostListener('window:beforeunload', ['$event'])
  // unloadHandler(event: Event): boolean {
  //   if (Capacitor.getPlatform() === 'web') {
  //     const eventTargetDoc = event.target as Document;
  //     if (!eventTargetDoc.activeElement['href']) {
  //       this.unsubscriberService.unsubscribeAll();
  //     }
  //   }

  //   return false;
  // }

  async ngOnInit() {
    await this.globalService.checkIfUpdateIsAvailable();

    this.globalService.disableLogOnProd(environment.production);

    // this.listenToAppUrlOpen();

    // this.currentUser$.pipe(
    //   filter<User>(Boolean),
    //   map((user) => this.userService.initFcm(user)),
    //   tap((user) => {
    //     if (this.currentUserRole && this.currentUserRole !== user.role) {
    //       this.curr
    //     }
    //   })
    // );
  }

  ngOnDestroy() {
    this.unsubscriberService.unsubscribeAll();
  }

  listenToAppUrlOpen() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        console.log('appUrlOpen: ', event.url);
        const url = new URL(event.url);

        const customLink = url.searchParams.get('link');
        if (customLink) {
          try {
            const link = new URL(customLink);
            console.log('appUrlOpen custom link decoded: ', link.pathname + link.search);
            window.location.replace(link.pathname + link.search);
            return;
          } catch (e) {
            console.error('Invalid custom link:', e);
          }
        }

        // Handle Firebase action code links (email verification, password reset, etc)
        // Firebase sends links like: https://app.link-up.fr/?oobCode=XXX&mode=verifyEmail
        const mode = url.searchParams.get('mode');
        const oobCode = url.searchParams.get('oobCode');

        if (mode && oobCode) {
          console.log('Firebase action link detected - mode:', mode, 'code:', oobCode);
          // Route to the in-app action page — query params are read by action.component.ts
          window.location.replace(`/login/action?mode=${mode}&oobCode=${oobCode}`);
          return;
        }

        // Default: treat entire URL path and search params as the navigation target
        console.log('appUrlOpen standard link decoded: ', url.pathname + url.search);
        window.location.replace(url.pathname + url.search);
      });
    });
  }

  async logout() {
    if (!(await this.globalService.confirmLogout())) {
      return;
    }
    await this.userService.logout();
  }
}
